import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit, ViewChild, computed, signal } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { validateAndFocusFirstError } from '../../utils/form-validation';
import { friendlyError } from '../../utils/friendly-error';

import { AdminTabOption, AdminTabsComponent } from '../ui/admin-tabs.component';
import { CooldownBubbleDirective } from '../../directives/cooldown-bubble.directive';
import { BankSelectComponent } from '../ui/bank-select.component';
import { PaginationComponent } from '../ui/pagination.component';

import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { ActionCooldownService } from '../../services/action-cooldown.service';
import {
  BusinessPayoutMethod,
  PayoutMethodPayload,
  PayoutOverview,
  PayoutService,
} from '../../services/payout.service';
import { EsDatePipe } from '../../shared/es-date.pipe';

type ModalMode = 'create' | 'edit' | 'primary' | 'archive' | null;
type IncomeTab = 'metodos' | 'reservas' | 'transferencias' | 'deuda';

const emptyForm = (): PayoutMethodPayload => ({
  label: '',
  method_type: 'bank_transfer',
  institution_name: '',
  bank_id: null,
  account_holder: '',
  account_number: '',
  bank_account_type: 'checking',
  is_primary: false,
});

@Component({
  selector: 'app-ingresos-negocio',
  standalone: true,
  imports: [EsDatePipe, CommonModule, FormsModule, AdminTabsComponent, BankSelectComponent, CooldownBubbleDirective, PaginationComponent],
  templateUrl: './ingresos-negocio.component.html',
  styleUrl: './ingresos-negocio.component.css',
})
export class IngresosNegocioComponent implements OnInit {
  readonly incomeTabs: readonly AdminTabOption[] = [
    {
      id: 'metodos',
      label: 'Métodos de cobro',
      description: 'Cuentas y destinos',
      icon: 'bi-credit-card-2-front',
      tabId: 'tab-metodos-de-cobro',
      panelId: 'panel-metodos-de-cobro',
    },
    {
      id: 'reservas',
      label: 'Pagos de reservas',
      description: 'Cobros recibidos',
      icon: 'bi-calendar2-check',
      tabId: 'tab-pagos-de-reservas',
      panelId: 'panel-pagos-de-reservas',
    },
    {
      id: 'transferencias',
      label: 'Transferencias',
      description: 'Historial de entregas',
      icon: 'bi-arrow-left-right',
      tabId: 'tab-transferencias',
      panelId: 'panel-transferencias',
    },
    {
      id: 'deuda',
      label: 'Comisión pendiente',
      description: 'Deuda con TuSala',
      icon: 'bi-receipt-cutoff',
      tabId: 'tab-comision-pendiente',
      panelId: 'panel-comision-pendiente',
    },
  ];
  readonly activeTab = signal<IncomeTab>('metodos');
  readonly data = signal<PayoutOverview | null>(null);
  readonly loading = signal(true);
  readonly paymentsPage = signal(1);
  readonly paymentsLimit = signal(25);
  readonly settlementsPage = signal(1);
  readonly settlementsLimit = signal(25);
  readonly reservationPaymentsPages = computed(() =>
    Math.max(1, Math.ceil((this.data()?.reservation_payments_total || 0) / (this.data()?.reservation_payments_limit || this.paymentsLimit()))),
  );
  readonly settlementsPages = computed(() =>
    Math.max(1, Math.ceil((this.data()?.settlements_total || 0) / (this.data()?.settlements_limit || this.settlementsLimit()))),
  );
  readonly saving = signal(false);
  readonly modal = signal<ModalMode>(null);
  readonly selectedMethod = signal<BusinessPayoutMethod | null>(null);
  readonly availableBalance = computed(() => this.data()?.balance.available ?? 0);
  // Aclara de qué está hecho "Total por recibir de TuSala" cuando incluye
  // una liquidación ya agrupada (a un % de comisión que pudo fijarse en
  // OTRO momento) además de lo vigente sin agrupar -- sin esto, esta
  // tarjeta y "TuSala te debe" (más abajo, solo lo vigente) pueden mostrar
  // cifras distintas sin explicación visible, aunque ambas sean correctas
  // para lo que cada una describe.
  pendingBreakdownLabel(): string | null {
    const balance = this.data()?.balance;
    const inSettlement = Number(balance?.in_settlement || 0);
    if (inSettlement <= 0) return null;
    const fmt = (n: number) => `$${n.toFixed(2)}`;
    return `Incluye ${fmt(inSettlement)} ya agrupados en una liquidación pendiente de transferir, más ${fmt(Number(balance?.unallocated || 0))} del periodo vigente sin agrupar.`;
  }
  readonly origin = computed(() => this.data()?.origin_breakdown ?? null);
  readonly hasExternalIncome = computed(() => Number(this.origin()?.external_gross ?? 0) > 0);
  // Posición de liquidación: si la empresa le debe al negocio o el negocio le
  // debe a la empresa por comisión no cubierta. Nunca ambos a la vez.
  readonly companyOwesBusiness = computed(() => Number(this.origin()?.company_owes_business ?? 0));
  readonly businessOwesCompany = computed(() => Number(this.origin()?.business_owes_company ?? 0));
  // Total FACTURADO por el negocio (bruto, antes de comisión) — se muestra
  // junto al neto para que quede claro que la comisión ya se descuenta del
  // total que paga el cliente, no de otra cosa. Viene ya calculado del
  // backend (total_gross_generated, sobre el histórico COMPLETO) -- sumar
  // aquí solo reservation_payments daría un total incompleto en cuanto esa
  // lista quedó paginada (solo reflejaría la página visible).
  readonly totalGeneratedGross = computed(() => Number(this.data()?.total_gross_generated ?? 0));
  // Historial de liquidaciones de DEUDA (negocio -> TuSala) -- compartido tal
  // cual con la pantalla de super admin (mismo pedido del usuario). Solo
  // lectura para el propietario, salvo el botón "Pagar ahora" en la
  // pendiente, que genera su propio enlace de pago (self-service).
  readonly debtSettlements = computed<any[]>(() => this.data()?.debt_settlements ?? []);
  readonly pendingDebtSettlement = computed(() => this.debtSettlements().find((s: any) => s.status === 'pending') ?? null);
  readonly payingDebtSettlementId = signal<number | null>(null);
  readonly closingCurrentDebt = signal(false);

  payDebtSettlementNow(settlement: any): void {
    if (this.payingDebtSettlementId() === settlement.id) return;
    this.payingDebtSettlementId.set(settlement.id);
    this.api.getOwnDebtSettlementPaymentLink(this.businessId, settlement.id).subscribe({
      next: (response) => {
        this.payingDebtSettlementId.set(null);
        if (response?.paymentLinkUrl) window.open(response.paymentLinkUrl, '_blank', 'noopener');
        else this.notifications.error('No fue posible obtener el enlace de pago.');
      },
      error: (error) => {
        this.payingDebtSettlementId.set(null);
        this.notifications.error(friendlyError(error, 'No fue posible obtener el enlace de pago.'));
      },
    });
  }

  // Corrección pedida: antes solo se podía pagar una deuda si un super admin
  // ya la había cerrado a mano con los calendarios -- aunque la deuda
  // vigente (businessOwesCompany) ya se veía en pantalla y el mes en curso
  // ni siquiera hubiera terminado. Este botón cierra el periodo automático
  // vigente y abre el enlace de pago en el mismo paso.
  //
  // Bug real corregido: si YA existía una liquidación 'pending' (de un click
  // anterior), este método se saltaba por completo -- el botón "Pagar ahora"
  // abría directo el enlace de esa liquidación VIEJA (payDebtSettlementNow),
  // sin antes sumarle la deuda nueva generada después. El propietario veía
  // "$23.40" en el resumen pero el enlace solo cobraba los "$7.80" de la
  // primera liquidación. Ahora SIEMPRE se llama al backend primero (que
  // acumula cualquier deuda nueva en la liquidación existente, ver
  // createDebtSettlementForBusiness) y solo después se abre el enlace ya
  // actualizado -- nunca se salta directo al enlace viejo.
  payCurrentDebtNow(): void {
    if (this.closingCurrentDebt()) return;
    this.closingCurrentDebt.set(true);
    this.api.createOwnDebtSettlement(this.businessId).subscribe({
      next: (response) => {
        this.closingCurrentDebt.set(false);
        if (response?.paymentLinkUrl) window.open(response.paymentLinkUrl, '_blank', 'noopener');
        else this.notifications.warning('Se cerró el periodo, pero no fue posible generar el enlace de pago automáticamente.');
        this.load();
      },
      error: (error) => {
        this.closingCurrentDebt.set(false);
        this.notifications.error(friendlyError(error, 'No fue posible cerrar y cobrar la deuda vigente.'));
      },
    });
  }

  readonly transferHistory = computed<any[]>(() => {
    const grouped = new Map<string, any>();
    const settlements = this.data()?.settlements || [];
    for (const transfer of settlements.flatMap((settlement: any) => settlement.transfers || [])) {
      const minute = Math.floor(new Date(transfer.performed_at).getTime() / 60000);
      const key = `${transfer.transfer_reference || 'sin-referencia'}|${transfer.performed_by_user_id}|${minute}|${transfer.notes || ''}`;
      const current = grouped.get(key);
      if (current) current.amount = Number(current.amount) + Number(transfer.amount);
      else grouped.set(key, { ...transfer, amount: Number(transfer.amount) });
    }
    return [...grouped.values()].sort(
      (a: any, b: any) => new Date(b.performed_at).getTime() - new Date(a.performed_at).getTime(),
    );
  });

  form: PayoutMethodPayload = emptyForm();

  constructor(
    private readonly auth: AuthService,
    private readonly api: PayoutService,
    private readonly notifications: NotificationService,
    private readonly cooldown: ActionCooldownService,
  ) {}

  get businessId(): number {
    return Number((this.auth.currentUser() as any)?.id);
  }

  ngOnInit(): void {
    this.syncTabFromHash(true);
    this.load();
  }

  selectTab(tab: string): void {
    if (!this.isIncomeTab(tab)) return;

    this.activeTab.set(tab);
    const hash = this.hashForTab(tab);
    const nextUrl = `${window.location.pathname}${window.location.search}#${hash}`;
    window.history.pushState(window.history.state, '', nextUrl);
  }

  @HostListener('window:hashchange')
  @HostListener('window:popstate')
  onHistoryNavigation(): void {
    this.syncTabFromHash(false);
  }

  private syncTabFromHash(replaceInvalid: boolean): void {
    const tab = this.tabForHash(window.location.hash);
    this.activeTab.set(tab);

    if (replaceInvalid && window.location.hash !== `#${this.hashForTab(tab)}`) {
      const nextUrl = `${window.location.pathname}${window.location.search}#${this.hashForTab(tab)}`;
      window.history.replaceState(window.history.state, '', nextUrl);
    }
  }

  private tabForHash(hash: string): IncomeTab {
    const normalized = decodeURIComponent(hash.replace(/^#/, '')).toLowerCase();
    if (normalized === 'pagos-de-reservas') return 'reservas';
    if (normalized === 'transferencias') return 'transferencias';
    if (normalized === 'comision-pendiente') return 'deuda';
    return 'metodos';
  }

  private hashForTab(tab: IncomeTab): string {
    if (tab === 'reservas') return 'pagos-de-reservas';
    if (tab === 'transferencias') return 'transferencias';
    if (tab === 'deuda') return 'comision-pendiente';
    return 'metodos-de-cobro';
  }

  private isIncomeTab(tab: string): tab is IncomeTab {
    return tab === 'metodos' || tab === 'reservas' || tab === 'transferencias' || tab === 'deuda';
  }

  load(): void {
    if (!this.businessId) {
      const message = 'No se encontró un negocio activo para consultar las transferencias.';
      this.loading.set(false);
      this.notifications.error(message);
      return;
    }
    this.loading.set(true);
    this.api.business(this.businessId, this.paymentsPage(), this.paymentsLimit(), this.settlementsPage(), this.settlementsLimit()).subscribe({
      next: (response) => {
        this.data.set(response);
        this.loading.set(false);
      },
      error: (error) => {
        const message = friendlyError(error, 'No se pudo cargar la información financiera.');
        this.notifications.error(message);
        this.loading.set(false);
      },
    });
  }

  goToPaymentsPage(page: number): void {
    if (this.loading() || page < 1 || page > this.reservationPaymentsPages() || page === this.paymentsPage()) return;
    this.paymentsPage.set(page);
    this.load();
  }

  goToSettlementsPage(page: number): void {
    if (this.loading() || page < 1 || page > this.settlementsPages() || page === this.settlementsPage()) return;
    this.settlementsPage.set(page);
    this.load();
  }

  private static readonly LETTERS_RE = /^[a-zA-ZÀ-ÖØ-öø-ÿ\s]$/;
  private static readonly DIGITS_RE = /^[0-9]$/;

  blockNonLetters(evt: KeyboardEvent): void {
    if (evt.ctrlKey || evt.metaKey || evt.altKey || evt.key.length > 1) return;
    if (!IngresosNegocioComponent.LETTERS_RE.test(evt.key)) evt.preventDefault();
  }

  blockNonDigits(evt: KeyboardEvent): void {
    if (evt.ctrlKey || evt.metaKey || evt.altKey || evt.key.length > 1) return;
    if (!IngresosNegocioComponent.DIGITS_RE.test(evt.key)) evt.preventDefault();
  }

  onHolderPaste(): void {
    setTimeout(() => {
      this.form.account_holder = this.form.account_holder.replace(/[^a-zA-ZÀ-ÖØ-öø-ÿ\s]/g, '');
    });
  }

  onAccountNumberPaste(): void {
    setTimeout(() => {
      this.form.account_number = (this.form.account_number || '').replace(/[^0-9]/g, '');
    });
  }

  openCreate(): void {
    this.form = emptyForm();
    this.selectedMethod.set(null);
    this.bankSelectionError.set(false);
    this.modal.set('create');
  }

  openEdit(method: BusinessPayoutMethod): void {
    this.selectedMethod.set(method);
    this.form = {
      label: method.label,
      method_type: 'bank_transfer',
      institution_name: method.institution_name,
      bank_id: method.bank_id ?? null,
      account_holder: method.account_holder,
      account_number: method.account_number || '',
      bank_account_type: method.bank_account_type || 'checking',
      is_primary: method.is_primary,
    };
    this.bankSelectionError.set(false);
    this.modal.set('edit');
  }

  requestPrimary(method: BusinessPayoutMethod): void {
    this.selectedMethod.set(method);
    this.modal.set('primary');
  }

  requestArchive(method: BusinessPayoutMethod): void {
    this.selectedMethod.set(method);
    this.modal.set('archive');
  }

  closeModal(): void {
    if (this.saving()) return;
    this.modal.set(null);
    this.selectedMethod.set(null);
  }

  @ViewChild('methodFormEl') methodFormEl!: ElementRef<HTMLFormElement>;
  @ViewChild('bankSelect') bankSelect!: BankSelectComponent;
  bankSelectionError = signal(false);

  save(methodForm: NgForm): void {
    if (!this.cooldown.consume('ingresos-save-method')) return;
    if (!validateAndFocusFirstError(methodForm, this.methodFormEl?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de guardar el método.')) return;
    if (!this.bankSelect?.hasValidSelection()) {
      this.bankSelectionError.set(true);
      this.notifications.error('Selecciona un banco de la lista o elige "Otro" e ingresa el nombre.');
      this.bankSelect?.focusSearch();
      return;
    }
    const editing = this.modal() === 'edit';
    const methodId = this.selectedMethod()?.id;
    if (editing && !methodId) return;

    this.saving.set(true);
    const request = editing
      ? this.api.updateMethod(this.businessId, methodId!, this.form)
      : this.api.saveMethod(this.businessId, this.form);

    request.subscribe({
      next: () => {
        this.saving.set(false);
        this.modal.set(null);
        const message = editing
          ? 'Método de cobro actualizado correctamente.'
          : 'Método de cobro creado correctamente.';
        this.notifications.show(
          message,
          'success',
          4500,
          editing ? `payout-method-update:${methodId}` : 'payout-method-create',
        );
        this.load();
        window.dispatchEvent(new CustomEvent('tusala:payout-methods-changed'));
      },
      error: (error) => {
        this.saving.set(false);
        const message = friendlyError(error, 'No se pudo guardar el método.');
        this.notifications.error(message);
      },
    });
  }

  confirmPrimary(): void {
    if (!this.cooldown.consume('ingresos-primary')) return;
    const method = this.selectedMethod();
    if (!method) return;
    this.saving.set(true);
    this.api.primary(this.businessId, method.id).subscribe({
      next: () => this.finishAction('Método principal actualizado.', `payout-primary:${method.id}`),
      error: (error) => this.failAction(error, 'No se pudo cambiar el método principal.'),
    });
  }

  confirmArchive(): void {
    if (!this.cooldown.consume('ingresos-archive')) return;
    const method = this.selectedMethod();
    if (!method) return;
    this.saving.set(true);
    this.api.archive(this.businessId, method.id).subscribe({
      next: () => this.finishAction('Método desactivado correctamente.', `payout-archive:${method.id}`),
      error: (error) => this.failAction(error, 'No se pudo desactivar el método.'),
    });
  }

  methodType(value: string): string {
    return {
      bank_transfer: 'Transferencia bancaria',
      cash: 'Efectivo',
    }[value] || value;
  }

  trackMethod(_index: number, method: BusinessPayoutMethod): number {
    return method.id;
  }

  trackPayment(_index: number, payment: any): number | string {
    return payment.id || payment.payment_id || payment.reservation_id;
  }

  trackTransfer(_index: number, transfer: any): number | string {
    return transfer.id || `${transfer.transfer_reference || ''}:${transfer.performed_at}`;
  }

  private finishAction(message: string, actionKey: string): void {
    this.saving.set(false);
    this.modal.set(null);
    this.selectedMethod.set(null);
    this.notifications.show(message, 'success', 4500, actionKey);
    this.load();
    window.dispatchEvent(new CustomEvent('tusala:payout-methods-changed'));
  }

  private failAction(error: any, fallback: string): void {
    this.saving.set(false);
    const message = friendlyError(error, fallback);
    this.notifications.error(message);
  }
}
